/**
 * Global Error Handler - Window-level unhandled error and rejection capture.
 *
 * WHAT THIS COVERS:
 *   - Promises rejected without a .catch() handler  (unhandledrejection)
 *   - Synchronous errors thrown outside the React tree  (window.onerror)
 *
 * WHAT THIS DOES NOT COVER (handled elsewhere):
 *   - React render errors → AppErrorBoundary (module:error, moduleId="app")
 *   - Editor panel crashes → ErrorGuard (crash:detected)
 *   - Route-level crashes → ErrorGuard[name="route"] (crash:detected)
 *
 * EXPORTED HANDLERS:
 *   handleUnhandledRejection and handleGlobalError are exported separately so
 *   tests can call them directly without registering real window listeners.
 *
 * USAGE:
 *   Call attachGlobalErrorHandlers() once in main.tsx after render.
 *   Safe to call multiple times (idempotent via flag).
 */

import { logger }   from "@/system/logger";
import { eventBus } from "@/runtime/eventBus";

export function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const msg =
    event.reason instanceof Error
      ? event.reason.message
      : String(event.reason ?? "Unknown async error");

  logger.error("Global unhandled promise rejection", { reason: msg });
  eventBus.emit({ type: "module:error", moduleId: "global-async", error: msg });
}

export function handleGlobalError(
  message: string | Event,
  source?:  string,
  _lineno?: number,
  _colno?:  number,
  error?:   Error,
): boolean {
  const msg = error?.message ?? String(message ?? "Unknown sync error");

  logger.error("Global uncaught error", { message: msg, source });
  eventBus.emit({ type: "module:error", moduleId: "global-sync", error: msg });

  // Return false - do NOT suppress the default browser error reporting.
  return false;
}

let attached = false;

export function attachGlobalErrorHandlers(): void {
  if (attached) return;
  attached = true;

  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  window.onerror = handleGlobalError;
}

/** For test isolation - resets the attachment guard. */
export function _resetHandlerState(): void {
  attached = false;
}
